import checkIcon from '@raison/static/icons/check-line.svg';
import React, {ReactNode, useMemo} from 'react';
import {RsnDialog} from '../../Components';
import InlineSvg from '../InlineSvg/InlineSvg';
import {IconProp} from '../shared/props';
import {Hint, HintType as HintTypeT, OptionObject, propertyAsKey, selfAsKey} from './RsnPicker.shared';

export interface RsnPickerDialogProps<T> {
    title?: string;
    value?: T | null;
    onChange: (value: T) => void;
    options: OptionObject<T>[];
    keyedBy?: keyof T;
    empty?: ReactNode;
    footer?: ReactNode;
}

export namespace RsnMethodPicker {
    export type Props<T> = RsnPickerDialogProps<T>;
    export type Option<T> = OptionObject<T>;
    export type HintType = HintTypeT;
}

export function RsnPickerDialog<T>({
    title = 'Select option',
    options,
    value,
    onChange,
    keyedBy,
    empty = 'No options',
    footer,
}: RsnPickerDialogProps<T>) {
    const valueAsKey = keyedBy ? propertyAsKey(keyedBy) : selfAsKey<T>();
    const matchingOption = useMemo(
        () => (value == null ? null : options.find(opt => valueAsKey(opt.value) === valueAsKey(value))),
        [value, options, keyedBy]
    );

    const isSelectedValue = (value: T) => {
        return matchingOption?.value && valueAsKey(matchingOption.value) === valueAsKey(value);
    };

    return (
        <RsnDialog.Window placement='bottom'>
            <RsnDialog.LeftHeader>{title}</RsnDialog.LeftHeader>
            <RsnDialog.Body padding={'none'} align={'left'}>
                {options.length ? (
                    options.map(option => (
                        <RsnPickerOption
                            option={option}
                            isSelected={isSelectedValue(option.value)}
                            key={valueAsKey(option.value)}
                            onClick={onChange}
                        />
                    ))
                ) : (
                    <div className={'w-full p-4 text-center text-neutral-60'}>{empty}</div>
                )}
            </RsnDialog.Body>
            {!!footer && <RsnDialog.Footer>{footer}</RsnDialog.Footer>}
        </RsnDialog.Window>
    );
}

export interface OptionProps<T> {
    option: OptionObject<T>;
    isSelected?: boolean;
    onClick?: (value: T) => void;
}

function RsnPickerOption<T>({option, isSelected, onClick}: OptionProps<T>) {
    return (
        <button
            className={'flex w-full gap-3 p-4 text-left leading-none active:bg-neutral-20'}
            onClick={() => onClick?.(option.value)}
        >
            {option.icon && <figure className='size-9 shrink-0'>{IconProp.render(option.icon)}</figure>}
            <div className={'flex flex-1 flex-col justify-center'}>
                <div className={'fv-text-xl-regular text-neutral-100'}>{option.title}</div>
                {option.hint && <Hint type={option.hintType}>{option.hint}</Hint>}
            </div>
            <div className={'flex h-9 w-5 items-center'}>
                {isSelected ? <InlineSvg src={checkIcon} className={'size-5 text-primary-30'} /> : null}
            </div>
        </button>
    );
}
